import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { HomeSection, SectionProducts } from "@/api/types";

export function useHome(stateId: number | null | undefined, preview?: string | null) {
  return useQuery({
    queryKey: ["home", stateId ?? 0, preview ?? ""],
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/home/", {
          params: { query: { ...(stateId ? { state: stateId } : {}), ...(preview ? { preview } : {}) } },
        }),
      ),
    staleTime: preview ? 0 : 60_000,
  });
}

export function usePlans() {
  return useQuery({ queryKey: ["plans"], queryFn: () => unwrap(api.GET("/api/v1/plans/")), staleTime: 10 * 60_000 });
}

type SectionQuery = { cursor?: string; sub?: string; state?: number };

function fetchSectionPage(pk: string, cursor: string, sub: string | null, stateId: number | null): Promise<SectionProducts> {
  const query: SectionQuery = {};
  if (cursor) query.cursor = cursor;
  if (sub) query.sub = sub;
  if (stateId) query.state = stateId;
  // Typed loosely: the generated client hits TS's instantiation depth limit on this path.
  const get = api.GET as unknown as (
    path: string,
    init: { params: { path: { pk: string }; query: SectionQuery } },
  ) => Promise<{ data?: SectionProducts; error?: unknown; response: Response }>;
  return unwrap(get("/api/v1/home/sections/{pk}/products/", { params: { path: { pk }, query } }));
}

/**
 * Products for one home section: lazy (only once `enabled`), filtered by a subcategory chip, and paged
 * as the rail scrolls. Inline products from the feed seed the first page so nothing refetches.
 */
export function useSectionProducts(
  section: HomeSection,
  { sub, stateId, enabled }: { sub: string | null; stateId: number | null; enabled: boolean },
) {
  const seeded = !sub && !section.lazy;
  return useInfiniteQuery({
    queryKey: ["home-section", section.key, sub ?? "", stateId ?? 0],
    queryFn: ({ pageParam }) => fetchSectionPage(section.key, pageParam, sub, stateId),
    initialPageParam: "",
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    initialData: seeded
      ? { pages: [{ results: section.products, next_cursor: section.next_cursor ?? null }], pageParams: [""] }
      : undefined,
    enabled: enabled || seeded,
    staleTime: 60_000,
  });
}

export function usePersonalRails(recent: string[], enabled: boolean) {
  return useQuery({
    queryKey: ["home-personal", recent.join(",")],
    queryFn: () => unwrap(api.GET("/api/v1/home/personal/", { params: { query: recent.length ? { recent: recent.join(",") } : {} } })),
    enabled,
    staleTime: 60_000,
  });
}
